import { useState, useCallback, useEffect, useRef } from "react";
import * as api from "../api";

// Cronómetro de tarea activa del usuario.
//
// La verdad está en el SERVIDOR, no aquí: el cronómetro en curso y los que
// quedan en pausa viven en Postgres, así que cerrar la pestaña, recargar o
// entrar desde otro equipo tiene que dar el mismo número. Este hook solo pinta
// lo que el servidor dice y le manda órdenes.
//
// `onFueraDeJornada` se llama cuando el servidor rechaza un arranque porque no
// estás fichado: el caso real es haber fichado salida en Odoo sin que ALM se
// haya enterado, y entonces hay que revalidar el fichaje.
export function useCronometro(token, showToast, onFueraDeJornada) {
  const [dia,        setDia]        = useState(null);
  const [activo,     setActivo]     = useState(null);
  const [pausados,   setPausados]   = useState([]);
  const [minutosHoy, setMinutosHoy] = useState(0);
  const [cargando,   setCargando]   = useState(false);
  const [guardando,  setGuardando]  = useState(false);
  // null = aún no se sabe. false = al servidor le falta la migración, así que el
  // widget se esconde y el resto del panel sigue funcionando igual.
  const [disponible, setDisponible] = useState(null);
  // Se descartó el cierre de jornada habiendo tiempo sin anotar. Es el único
  // estado de esta función que no está en el servidor.
  const [pendienteCierre, setPendienteCierre] = useState(false);

  // Evita que dos peticiones en vuelo se pisen al escribir el estado.
  const enVuelo = useRef(false);

  // Un fallo de carga se avisa UNA vez. Sin esto, cada vuelta a la pestana
  // reintentaba y volvia a gritar: con el backend caido salian diez avisos
  // iguales seguidos y tapaban el que importaba.
  const avisado = useRef(false);

  // Desfase entre el reloj del servidor y el del navegador. `tramo_desde` es
  // hora del servidor: en un equipo con la hora mal puesta el contador salía con
  // minutos de más, y al recargar daba un número distinto al de antes.
  const desfase = useRef(0);

  const aplicar = d => {
    if (d.ahora) desfase.current = Date.parse(d.ahora) - Date.now();
    setDia(d.dia || null);
    setActivo(d.activo || null);
    setPausados(d.pausados || []);
    setMinutosHoy(d.minutos_hoy || 0);
    setDisponible(true);
  };

  const manejar = (e, porDefecto) => {
    // En una ESCRITURA, MIGRACION se dice en voz alta y el widget NO se esconde.
    //
    // Esconderlo es lo correcto cuando falla la carga inicial —la funcion no
    // esta disponible y no hay nada que ofrecer—, pero aqui el usuario acaba de
    // pulsar algo: se quedaba sin aviso, el widget desaparecia, la siguiente
    // consulta lo devolvia (el GET no toca la columna que falta) y volvia
    // VACIO, borrando la descripcion recien escrita. Desde fuera: "le doy a
    // empezar y no pasa nada".
    if (e?.code === "MIGRACION") {
      showToast(e.message || "Al servidor le falta una migracion del cronometro", "warn");
      return;
    }
    if (e?.code === "FUERA") {
      showToast("Ficha entrada antes de empezar a cronometrar", "warn");
      if (onFueraDeJornada) onFueraDeJornada();
      return;
    }
    if (e?.code === "SIN_CONEXION") {
      showToast("Conecta tu cuenta de Odoo para poder cronometrar", "warn");
      return;
    }
    // Ese 409 trae el estado real: la pestaña se recoloca en vez de quedarse
    // pintando un cronómetro que ya no existe.
    if (e?.code === "YA_ACTIVO" || e?.code === "ESTADO") {
      showToast(e.message, "warn");
      return;
    }
    if (e?.code === "TAREA_BLOQUEADA") {
      const espera = (e.bloqueada_por || []).map(x => `«${x.title}»`).join(" y ");
      showToast(espera ? `Ese paso espera a ${espera}` : e.message, "warn");
      return;
    }
    showToast(e?.message || porDefecto, "warn");
  };

  const cargar = useCallback(async () => {
    if (!token || enVuelo.current) return;
    enVuelo.current = true;
    setCargando(true);
    try {
      aplicar(await api.getCronometro());
      avisado.current = false;          // volvio a responder: se puede volver a avisar
    } catch (e) {
      if (e?.code === "MIGRACION") { setDisponible(false); }
      else if (!avisado.current) {
        avisado.current = true;
        showToast(e?.message || "No se pudieron cargar tus cronómetros", "warn");
      }
    } finally {
      enVuelo.current = false;
      setCargando(false);
    }
  }, [token]); // eslint-disable-line

  // Las escrituras son CONFIRMADAS, no optimistas: todas devuelven el estado
  // completo y se aplica tal cual. `tramo_desde` lo pone el servidor, y
  // adivinarlo aquí haría que el número saltase al llegar la respuesta.
  const escribir = async (fn, porDefecto) => {
    if (!token || guardando) return null;
    setGuardando(true);
    try {
      const d = await fn();
      aplicar(d);
      return d;
    } catch (e) {
      manejar(e, porDefecto);
      // Si el error trae estado (los 409), se aprovecha para recolocarse.
      if (e?.activo !== undefined || e?.pausados) aplicar(e);
      else await cargar();
      return null;
    } finally { setGuardando(false); }
  };

  // El arranque puede traer un aviso sin ser un fallo: hoy, que quien pidio la
  // incidencia no es usuario de ALM y por eso la entrada del cuaderno se queda
  // sin destinatario.
  const iniciar = d =>
    escribir(async () => {
      const r = await api.iniciarCronometro(d);
      if (r.aviso_destinatario) showToast(r.aviso_destinatario, "warn");
      return r;
    }, "No se pudo empezar la tarea");
  const pausar  = id => escribir(() => api.pausarCronometro(id), "No se pudo pausar");
  const reanudar = id => escribir(() => api.reanudarCronometro(id), "No se pudo reanudar");

  const parar = (id, finalizarTarea = false) =>
    escribir(async () => {
      const d = await api.pararCronometro(id, { finalizar_tarea: finalizarTarea });
      if (d.tarea && d.tarea.ok === false) showToast(d.tarea.motivo, "warn");
      else if (finalizarTarea && d.tarea?.ok) showToast("Tarea cerrada en el proyecto");
      else showToast("Anotado en el cuaderno");
      return d;
    }, "No se pudo cerrar la tarea");

  const descartar = id =>
    escribir(async () => {
      const d = await api.borrarCronometro(id);
      showToast(d.entrada_conservada
        ? "Cronómetro descartado. La entrada que ya estaba en el cuaderno se conserva."
        : "Cronómetro descartado", "warn");
      return d;
    }, "No se pudo descartar");

  const pausarTodo = () => escribir(() => api.pausarCronometros(), "No se pudieron pausar");

  const cerrarJornada = completarTareas =>
    escribir(async () => {
      const d = await api.cerrarJornadaCronometros(completarTareas);
      (d.no_completadas || []).forEach(x => showToast(x.motivo, "warn"));
      const n = d.entradas?.length || 0;
      if (n) showToast(`Jornada cerrada: ${n} entrada${n === 1 ? "" : "s"} en el cuaderno`);
      setPendienteCierre(false);
      return d;
    }, "No se pudo cerrar la jornada");

  // El tiempo se CALCULA, no se acumula en el cliente: lo cerrado lo cerró el
  // servidor y el tramo abierto se mide desde SU marca. Así recargar es exacto y
  // dos pestañas muestran lo mismo.
  const msDe = useCallback((c, ahora = Date.now()) =>
    (c?.minutos_cerrados || 0) * 60000 +
    (c?.tramo_desde
      ? Math.max(0, ahora + desfase.current - Date.parse(c.tramo_desde))
      : 0), []);

  useEffect(() => { cargar(); }, [cargar]);

  // Al volver a la pestaña se revalida: el cronómetro puede haberse pausado
  // desde otro equipo, o el servidor puede haber cerrado el día por su cuenta.
  //
  // Solo `visibilitychange`, no también `focus`: los dos saltan al volver a la
  // pestaña, así que enganchar ambos era el doble de peticiones para el mismo
  // gesto. `visibilitychange` es el que distingue "pestaña oculta" de "otra
  // ventana encima", que es lo que aquí importa.
  useEffect(() => {
    const alVolver = () => { if (!document.hidden) cargar(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [cargar]);

  // Una pestaña abierta toda la noche se queda con el día de ayer y pintaría un
  // cronómetro que el cierre nocturno ya ha guardado. Se comprueba cada minuto:
  // es barato y sin esto el número que se ve es mentira.
  useEffect(() => {
    if (!dia) return;
    const id = setInterval(() => {
      const hoy = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });
      if (hoy !== dia) cargar();
    }, 60000);
    return () => clearInterval(id);
  }, [dia, cargar]);

  const abiertos = [...(activo ? [activo] : []), ...pausados];

  return {
    dia, activo, pausados, abiertos, minutosHoy, cargando, guardando, disponible,
    cargar, iniciar, pausar, reanudar, parar, descartar, pausarTodo, cerrarJornada,
    msDe, hayAbiertos: abiertos.length > 0,
    pendienteCierre, setPendienteCierre,
  };
}
